import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  previousWindow,
  resolveWindow,
  type RangePreset,
  type ScopeWindow,
} from '../utils/scopeWindow';

/* ============================================================
 * Global scope — one URL-backed state that every number on the
 * dashboard obeys. Because it lives in search params, any view is
 * deep-linkable (?range=custom&from=2026-06-01&to=2026-06-30
 * &loc=Tamil+Nadu&site=<locationId>&project=<id>) and the browser
 * back button walks back up the drill-down naturally.
 *
 * Drill hierarchy: India (1) → state (2) → location (3) → project (4).
 * ============================================================ */

const PRESETS: RangePreset[] = ['week', 'lastweek', 'month', '30d', 'custom'];
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

export type ScopeLevel = 1 | 2 | 3 | 4;

export interface Scope {
  preset: RangePreset;
  /** Resolved inclusive day window every query uses. */
  window: ScopeWindow;
  /** Equivalent preceding window (deltas). */
  prevWindow: ScopeWindow;
  /** Level 2 scope — a state name (e.g. "Tamil Nadu"). */
  state: string | null;
  /** Level 3 scope — a location id (one location holds several projects). */
  locationId: string | null;
  /** Level 4 scope — a project id. */
  projectId: string | null;
  /** Entity search text. */
  query: string;
  /** Drill level derived from state/location/project. */
  level: ScopeLevel;
}

export interface ScopeActions {
  setPreset: (preset: Exclude<RangePreset, 'custom'>) => void;
  setCustomWindow: (window: ScopeWindow) => void;
  drillToState: (state: string) => void;
  drillToLocation: (locationId: string) => void;
  drillToProject: (projectId: string) => void;
  /** Atomically set the whole selection (scope-bar dropdowns). */
  setSelection: (sel: { state: string | null; locationId: string | null; projectId: string | null }) => void;
  /** One level up (4 → 3 → 2 → 1). */
  drillUp: () => void;
  /** Jump straight to a level via the breadcrumb. */
  drillToLevel: (level: 1 | 2 | 3) => void;
  setQuery: (q: string) => void;
}

export function useScope(): [Scope, ScopeActions] {
  const [params, setParams] = useSearchParams();

  const scope = useMemo<Scope>(() => {
    const rawPreset = params.get('range') ?? 'week';
    const preset = (PRESETS.includes(rawPreset as RangePreset) ? rawPreset : 'week') as RangePreset;
    const from = params.get('from') ?? undefined;
    const to = params.get('to') ?? undefined;
    const custom =
      preset === 'custom' && from && DAY_RE.test(from) && to && DAY_RE.test(to)
        ? { from, to }
        : undefined;
    const window = resolveWindow(custom ? 'custom' : preset === 'custom' ? 'week' : preset, custom);
    const state = params.get('loc');
    const locationId = params.get('site');
    const projectId = params.get('project');
    return {
      preset: custom ? 'custom' : preset === 'custom' ? 'week' : preset,
      window,
      prevWindow: previousWindow(window),
      state,
      locationId,
      projectId,
      query: params.get('q') ?? '',
      level: projectId ? 4 : locationId ? 3 : state ? 2 : 1,
    };
  }, [params]);

  const mutate = useCallback(
    (updates: Record<string, string | null>, options?: { replace?: boolean }) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: options?.replace ?? false }
      );
    },
    [setParams]
  );

  const actions = useMemo<ScopeActions>(
    () => ({
      // Time / filter changes replace history — only drills should stack
      // entries, so Back always means "up one level", never "undo a filter".
      setPreset: (preset) =>
        mutate({ range: preset, from: null, to: null }, { replace: true }),
      setCustomWindow: (window) =>
        mutate({ range: 'custom', from: window.from, to: window.to }, { replace: true }),
      drillToState: (state) => mutate({ loc: state, site: null, project: null, q: null }),
      drillToLocation: (locationId) => mutate({ site: locationId, project: null, q: null }),
      drillToProject: (projectId) => mutate({ project: projectId, q: null }),
      setSelection: (sel) =>
        mutate({ loc: sel.state, site: sel.locationId, project: sel.projectId, q: null }),
      drillUp: () => {
        if (scope.projectId) mutate({ project: null });
        else if (scope.locationId) mutate({ site: null });
        else if (scope.state) mutate({ loc: null });
      },
      drillToLevel: (level) => {
        if (level === 1) mutate({ loc: null, site: null, project: null });
        else if (level === 2) mutate({ site: null, project: null });
        else mutate({ project: null });
      },
      setQuery: (q) => mutate({ q: q || null }, { replace: true }),
    }),
    [mutate, scope.projectId, scope.locationId, scope.state]
  );

  return [scope, actions];
}
